/**
 * HUD for the dominoes game. Reuses the shared single-game DOM shell but
 * relabels the slots so they read as a domino game (tile counts + status
 * + chain ends) instead of pair counts.
 */

export interface DominoHudOptions {
  onNewGame(): void;
  onReplay(): void;
}

export interface DominoHud {
  setCounts(youTiles: number, cpuTiles: number): void;
  setEnds(left: number | null, right: number | null): void;
  setStatus(text: string | null): void;
  startTimer(): void;
  resetTimer(): void;
  showWin(title: string, sub: string): void;
  hideWin(): void;
}

function fmtTime(ms: number): string {
  const sec = Math.floor(ms / 1000);
  const mm = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${mm}:${ss.toString().padStart(2, '0')}`;
}

export function createDominoHud(opts: DominoHudOptions): DominoHud {
  const movesEl = document.getElementById('moves')!;
  const remainingEl = document.getElementById('remaining')!;
  const timeEl = document.getElementById('time')!;
  const statusEl = document.getElementById('status')!;
  const winOverlay = document.getElementById('win-overlay')!;
  const winMovesEl = document.getElementById('win-moves')!;
  const winTimeEl = document.getElementById('win-time')!;
  const winTitleEl = document.getElementById('win-title');
  const winEyebrow = document.getElementById('win-eyebrow');
  const undoBtn = document.getElementById('undo') as HTMLButtonElement | null;
  const hintBtn = document.getElementById('hint') as HTMLButtonElement | null;

  // Undo + hint don't apply to a 2-player domino round — disable both.
  if (undoBtn) { undoBtn.disabled = true; undoBtn.style.display = 'none'; }
  if (hintBtn) { hintBtn.disabled = true; hintBtn.style.display = 'none'; }

  let startedAt = 0;
  let timerStarted = false;
  let lastWinSub = '';

  function tickClock() {
    if (!timerStarted) {
      timeEl.textContent = '0:00';
      return;
    }
    timeEl.textContent = fmtTime(performance.now() - startedAt);
  }
  setInterval(tickClock, 250);
  tickClock();

  document.getElementById('new-game')?.addEventListener('click', () => opts.onNewGame());
  document.getElementById('win-replay')?.addEventListener('click', () => opts.onReplay());

  const hud: DominoHud = {
    setCounts(youTiles, cpuTiles) {
      movesEl.textContent = `you ${youTiles} · cpu ${cpuTiles}`;
    },
    setEnds(left, right) {
      if (left === null || right === null) {
        remainingEl.textContent = 'chain 0';
      } else {
        remainingEl.textContent = `${left} … ${right}`;
      }
    },
    setStatus(text) {
      if (text) {
        statusEl.textContent = text;
        statusEl.classList.add('show');
      } else {
        statusEl.classList.remove('show');
      }
    },
    startTimer() {
      if (timerStarted) return;
      timerStarted = true;
      startedAt = performance.now();
      tickClock();
    },
    resetTimer() {
      timerStarted = false;
      startedAt = 0;
      tickClock();
    },
    showWin(title, sub) {
      lastWinSub = sub;
      if (winTitleEl) winTitleEl.textContent = title;
      if (winEyebrow) winEyebrow.textContent = sub;
      winMovesEl.textContent = sub;
      winTimeEl.textContent = timeEl.textContent ?? '0:00';
      // Hide the "moves" stat in the win card — replace with sub-text.
      const winMovesParent = winMovesEl.parentElement;
      const winMovesSmall = winMovesParent?.querySelector('small');
      if (winMovesSmall) winMovesSmall.textContent = 'result';
      winOverlay.classList.toggle('win-overlay--lose', title.toLowerCase().includes('cpu won') || title.toLowerCase().includes('blocked'));
      winOverlay.hidden = false;
    },
    hideWin() {
      winOverlay.classList.remove('win-overlay--lose');
      winOverlay.hidden = true;
    },
  };
  return hud;
}
